import React, { ReactNode, useMemo } from 'react';
import {
  MARKETS_UPDATED,
  SetupArea,
  SetupAreaVersionStatus,
  useMarketsAdminService,
  useSetupAreasAdminService
} from 'nekst-api';
import {
  FilterContext,
  Header5,
  ItemsList,
  List,
  LoadingAnimation,
  SimpleBlock,
  Text,
  TextLink,
  useFilterContext,
  useShowContextRequired,
  TextFilterInput,
} from 'ui-builder';
import {
  IconDeleteButton,
  IconType,
  OpenFormIconButton,
  RowRepeater,
} from 'features/nekst-widgets';
import useSettingsNavigator from '../../settingsNavigator';
import { ClickableBlock, useStatesHelper } from 'features/tasks-forms';
import styles from './Markets.module.scss';
import { UpdateMarketForm } from './UpdateMarketForm';
import { MarketVisibilityIcon } from './MarketVisibilityIcon';
import { useDataLoaderNew } from 'data-loader';
import { SettingsBreadcrumbs } from '../../settingsModule';
import { Breadcrumb } from '../../../shared/web/layout/Breadcrumbs';
import { useParams } from 'react-router-dom';
import InnerBlock from '../../../shared/web/layout/InnerBlock';
import { CreateMarketButton } from './CreateMarketForm';

function DeleteMarketButton() {
  const showContext = useShowContextRequired<SetupArea>();
  const marketsService = useMarketsAdminService();

  const isUsed = showContext.data.versions.some((version) => {
    return version.numberOfUsages > 0;
  });

  let message: string;
  if (isUsed) {
    message = 'The market is used by one or more teams. It\'ll be archived.';
  } else {
    message = `Are you sure you want to delete market "${showContext.data!.name}"?`;
  }

  const marketId = showContext.data.market?.id;

  if (marketId) {
    return (
      <IconDeleteButton
        deleteFunc={async () => {
          await marketsService.deleteMarket(showContext.data.market!.id);
        }}
        message={message}
        withConfirmationInput
      />
    );
  } else {
    return null;
  }
}

function SetupAreaRow() {
  const showContext = useShowContextRequired<SetupArea>();

  const { data } = showContext;

  const settingsNavigator = useSettingsNavigator();

  const getTitle = () => {
    if (data.market) {
      return `${data.market.state}: ${data.name}`;
    } else {
      return data.name;
    }
  };

  const sum = (items: number[]) => {
    return items.reduce((acc, curr) => acc + curr, 0);
  };

  const numberOfUsages = sum(data.versions.map((item) => item.numberOfUsages));

  return (
    <ClickableBlock
      title={(
        <>
          {getTitle()}
          <MarketVisibilityIcon />
        </>
      )}
      onClick={() => {
        settingsNavigator.openAdminSetupAreaPage(
          data.market?.state,
          data.id
        );
      }}
      buttons={data.market ? (
        <>
          <OpenFormIconButton
            form={<UpdateMarketForm setupArea={data} />}
            type={IconType.PENCIL}
            title="Edit Market"
          />
          <DeleteMarketButton />
        </>
      ) : undefined}
      content={(
        <SimpleBlock className={styles.links}>
          <Text>Used in teams: {numberOfUsages}</Text>
        </SimpleBlock>
      )}
    />
  );
}

type DataType = {
  notAssignedAreas: SetupArea[],
  usAssignedAreas: Record<string, SetupArea[]>,
  canadaAssignedAreas: Record<string, SetupArea[]>,
  setupAreas: SetupArea[],
  dataVersion: number
}

function useSetupAreasByStateDataLoading() {
  const setupAreasService = useSetupAreasAdminService();

  const {
    data: setupAreas,
    version: dataVersion
  } = useDataLoaderNew(
    async () => {
      return await setupAreasService.getSetupAreas();
    },
    {
      events: [
        MARKETS_UPDATED
      ]
    }
  );

  const statesHelper = useStatesHelper();

  return useMemo(() => {
    if (setupAreas) {
      const notAssignedAreas: SetupArea[] = [];
      const usAssignedAreas: Record<string, SetupArea[]> = {};
      const canadaAssignedAreas: Record<string, SetupArea[]> = {};

      setupAreas.forEach((area) => {
        const areaState = area.market?.state ?? '';

        const country = statesHelper.getCountryByValue(areaState);

        if (country === 'United States') {
          if (!usAssignedAreas[areaState]) {
            usAssignedAreas[areaState] = [];
          }
          usAssignedAreas[areaState].push(area);
        } else if (country === 'Canada') {
          if (!canadaAssignedAreas[areaState]) {
            canadaAssignedAreas[areaState] = [];
          }
          canadaAssignedAreas[areaState].push(area);
        } else {
          notAssignedAreas.push(area);
        }
      });

      return {
        notAssignedAreas,
        usAssignedAreas,
        canadaAssignedAreas,
        setupAreas,
        dataVersion
      };
    } else {
      return undefined;
    }
  }, [dataVersion]);
}

function GroupsMap(
  props: {
    title: string,
    map: Record<string, SetupArea[]>
  }
) {
  const statesHelper = useStatesHelper();

  const settingsNavigator = useSettingsNavigator();

  return (
    <>
      <Header5>{props.title}</Header5>
      {Object
        .entries(props.map)
        .sort(([stateA], [stateB]) =>
          statesHelper.getLabelByValue(stateA)
            .localeCompare(statesHelper.getLabelByValue(stateB))
        )
        .map(([state, areas]) => {
          return (
            <SimpleBlock>
              <TextLink
                onClick={() => {
                  settingsNavigator.openAdminStateMarketsPage(state);
                }}
                className={styles.stateLink}
              >
                {statesHelper.getLabelByValue(state)} ({areas.length.toString()})
              </TextLink>
              <ItemsList
                items={areas.map((area) => {
                  const isActive = area.versions.some((item) => item.status === SetupAreaVersionStatus.PUBLISHED)
                    return (
                      <>
                        <TextLink onClick={() => {
                          settingsNavigator.openAdminSetupAreaPage(state, area.id);
                        }}>
                          {area.name}
                        </TextLink>
                        {' '}
                        {isActive && (
                          <Text
                            className={styles.activeLabel}
                          >
                            Active
                          </Text>
                        )}
                      </>

                    );
                  }
                )}
              />
            </SimpleBlock>
          );
        })}
    </>
  );
}

function StateSetupAreasView(props: {
  setupAreas: SetupArea[],
  state: string
  dataVersion: number,
}) {
  const settingsNavigator = useSettingsNavigator();

  const statesHelper = useStatesHelper();

  return (
    <>
      <SettingsBreadcrumbs>
        <Breadcrumb text="Markets Administration" href={settingsNavigator.getAdminMarketsLink()}>
          <Breadcrumb text={statesHelper.getLabelByValue(props.state)} />
        </Breadcrumb>
      </SettingsBreadcrumbs>
      <List
        getDataFunc={async () => props.setupAreas}
        dependencies={[props.dataVersion]}
      >
        <RowRepeater blockClassName={styles.markets}>
          <SetupAreaRow />
        </RowRepeater>
      </List>
    </>
  );
}

function useStatesFilter(
  data: DataType
): DataType {
  const filter = useFilterContext<{ stateContains?: string }>();

  const stateContains = filter.filterValue?.stateContains;

  const statesHelper = useStatesHelper();

  if (stateContains) {
    return {
      notAssignedAreas: 'Without State'.toLowerCase()
        .includes(stateContains.toLowerCase()) ? data.notAssignedAreas : [],
      usAssignedAreas: Object.fromEntries(
        Object.entries(data.usAssignedAreas)
          .filter(([state, areas]) => {
            const stateLabel = statesHelper.getLabelByValue(state);
            return stateLabel.toLowerCase()
              .includes(stateContains.toLowerCase()) || areas.some(area => area.name.toLowerCase()
              .includes(stateContains.toLowerCase()));
          })
      ),
      canadaAssignedAreas: Object.fromEntries(
        Object.entries(data.canadaAssignedAreas)
          .filter(([state, areas]) => {
            const stateLabel = statesHelper.getLabelByValue(state);
            return stateLabel.toLowerCase()
              .includes(stateContains.toLowerCase()) || areas.some(area => area.name.toLowerCase()
              .includes(stateContains.toLowerCase()));
          })
      ),
      setupAreas: data.setupAreas,
      dataVersion: data.dataVersion
    };
  } else {
    return data;
  }
}

function StatesViewInner(props: {
  data: DataType
}) {
  const data = useStatesFilter(props.data);

  const settingsNavigator = useSettingsNavigator();

  return (
    <>
      <SettingsBreadcrumbs>
        <Breadcrumb text="Markets Administration" />
      </SettingsBreadcrumbs>
      {data.notAssignedAreas.length > 0 && (
        <SimpleBlock>
          <TextLink
            onClick={async () => settingsNavigator.openAdminStateMarketsPage(undefined)}
          >
            Without State ({data.notAssignedAreas.length.toString()})
          </TextLink>
        </SimpleBlock>
      )}
      {Object.keys(data.usAssignedAreas).length > 0 && (
        <GroupsMap title="Unites States" map={data.usAssignedAreas} />
      )}
      {Object.keys(data.canadaAssignedAreas).length > 0 && (
        <GroupsMap title="Canada" map={data.canadaAssignedAreas} />
      )}
    </>
  );

}

function StatesView(props: {
  data: DataType,
}) {
  return (
    <FilterContext>
      <TextFilterInput
        source="stateContains"
        placeholder="Search State Or Market"
        testId="state-filter"
        className={styles.filterInput}
      />
      <StatesViewInner data={props.data} />
    </FilterContext>
  );
}

export default function MarketsList() {

  const data = useSetupAreasByStateDataLoading();

  let { state } = useParams();

  state = state ? state.toUpperCase() : undefined;

  let content: ReactNode;

  let title: string;
  const statesHelper = useStatesHelper();

  if (data) {
    if (state) {
      let setupAreas = data.usAssignedAreas[state] || data.canadaAssignedAreas[state] || [];

      if (setupAreas.length === 0 && state === 'NOSTATE') {
        setupAreas = data.notAssignedAreas;
      }

      content = <StateSetupAreasView state={state} setupAreas={setupAreas}
                                     dataVersion={data.dataVersion} />;
      title = `Markets Administration - ${statesHelper.getLabelByValue(state) || 'Without State'}`;
    } else {
      content = (
        <StatesView data={data} />
      );
      title = 'Markets Administration';
    }
  } else {
    content = (<LoadingAnimation />);
    title = 'Markets Administration';
  }

  return (
    <InnerBlock title={title} headerRight={<CreateMarketButton />}>
      {content}
    </InnerBlock>
  );
}
